import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const SOCKET_PATH = "/api/socket.io";
const HB_EVERY_MS = 10000;   // heartbeat applicatif (ping avec accusé serveur)
const HB_ACK_MS = 4000;      // délai max d'accusé avant de déclarer le socket zombie
const ALIVE_MAX_MS = 30000;  // « Synchronisé » exige un signe de vie serveur plus récent que ça

// rooms: array of room ids to join on connect
// handlers: { eventName: fn, resync: fn }
// `connected` renvoyé = santé RÉELLE : transport ouvert + rooms rejointes + heartbeat récent.
export function useSocket(rooms, handlers) {
  const socketRef = useRef(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const [connected, setConnected] = useState(false);
  const roomsKey = JSON.stringify(rooms);

  useEffect(() => {
    const s = io(BACKEND_URL, {
      path: SOCKET_PATH,
      auth: (cb) => cb({ token: localStorage.getItem("novanext_token") || undefined }),
      // WebSocket direct : sans affinité de session entre instances, le long-polling (session liée
      // à un pod) casse le handshake/upgrade. Repli polling uniquement si le WebSocket est bloqué.
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    let wsFailures = 0;
    s.on("connect_error", () => {
      if (navigator.onLine === false) return; // coupure réseau : ne pas conclure que le WebSocket est bloqué
      wsFailures += 1;
      if (wsFailures >= 3 && s.io.opts.transports.length === 1) s.io.opts.transports = ["polling", "websocket"];
    });
    s.on("connect", () => { wsFailures = 0; s.io.opts.transports = ["websocket"]; });
    socketRef.current = s;

    const wanted = (rooms || []).filter(Boolean);
    const joined = new Set();
    let lastAlive = 0;
    let disposed = false;

    const computeHealth = () => {
      const ok = s.connected && joined.size >= wanted.length && Date.now() - lastAlive < ALIVE_MAX_MS;
      setConnected(ok);
    };
    const alive = () => { lastAlive = Date.now(); computeHealth(); };

    const joinRooms = () => {
      joined.clear();
      wanted.forEach((r) => s.emit("join_room", { room_id: r, client_session_id: localStorage.getItem("novanext_client_id") || undefined }));
      computeHealth();
    };

    let hasConnected = false;
    const doResync = () => {
      const fn = handlersRef.current?.resync;
      if (fn) fn();
    };
    s.on("connect", () => {
      alive();
      joinRooms();
      // Toute (re)connexion après la première déclenche une resync API complète (backend = vérité).
      if (hasConnected) doResync();
      hasConnected = true;
    });
    s.on("room_joined", (d) => { if (d?.room_id) joined.add(d.room_id); alive(); });
    s.on("room_denied", (d) => { console.warn("room denied", d?.room_id); });
    s.on("disconnect", () => { joined.clear(); setConnected(false); });
    s.on("connect_error", () => setConnected(false));

    const eventNames = Object.keys(handlersRef.current || {}).filter((e) => e !== "resync");
    eventNames.forEach((ev) => {
      s.on(ev, (data) => {
        alive();
        const fn = handlersRef.current?.[ev];
        if (fn) fn(data);
      });
    });

    // Zombie socket : après une veille OS/onglet, s.connected peut rester "true" alors que la
    // liaison est morte. On force un cycle disconnect→connect ; le handler `connect` rejoint
    // les rooms et déclenche la resync. Si le socket est sain, on resync quand même (état réel).
    let lastRecover = 0;
    const forceReconnect = () => {
      const now = Date.now();
      if (now - lastRecover < 3000) { doResync(); return; } // anti-tempête, mais jamais sans resync
      lastRecover = now;
      setConnected(false);
      try { s.disconnect(); } catch (e) {}
      try { s.connect(); } catch (e) {}
    };
    const onWake = () => { if (document.visibilityState === "visible") forceReconnect(); };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("online", forceReconnect);
    window.addEventListener("focus", onWake);
    window.addEventListener("pageshow", onWake);

    // Heartbeat applicatif : ping avec accusé serveur, même en arrière-plan (timers ralentis mais actifs).
    let hbTimer = null;
    const beat = () => {
      if (disposed) return;
      if (!s.connected) { computeHealth(); return; }
      let acked = false;
      try {
        s.timeout(HB_ACK_MS).emit("hb", (err, res) => {
          if (err || !res) return;
          acked = true;
          // Rooms perdues côté serveur (redémarrage backend) → re-join immédiat.
          const srvRooms = Array.isArray(res.rooms) ? res.rooms : null;
          if (srvRooms && wanted.some((r) => !srvRooms.includes(r))) { joinRooms(); doResync(); }
          alive();
        });
      } catch (e) {}
      hbTimer = setTimeout(() => { if (!acked && !disposed) forceReconnect(); }, HB_ACK_MS + 300);
    };
    const heartbeat = setInterval(beat, HB_EVERY_MS);
    // Contrôle de fraîcheur : bascule l'indicateur si plus aucun signe de vie récent.
    const freshness = setInterval(computeHealth, 5000);

    return () => {
      disposed = true;
      try {
        clearInterval(heartbeat);
        clearInterval(freshness);
        if (hbTimer) clearTimeout(hbTimer);
        document.removeEventListener("visibilitychange", onWake);
        window.removeEventListener("online", forceReconnect);
        window.removeEventListener("focus", onWake);
        window.removeEventListener("pageshow", onWake);
        s.removeAllListeners();
        s.disconnect();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsKey]);

  return { connected, socket: socketRef };
}
